/**
 * Rail Smoothing (SRVR-0355): the desktop editor's eleven rail smoothing algorithms
 * (`RailSmoothingAlgorithms.cs`), as pure functions over a rail's points — the head first, then its
 * nodes, each `[x, y, z]` with z the time in note units (`time_ms × 0.02`).
 *
 * Every algorithm moves X and Y only and keeps each point's z, except Catmull-Rom, which adds points
 * between the existing ones. The numbers match the desktop editor's, apart from the defects listed
 * at each function, which are fixed here rather than reproduced (docs/changes/feat-vr-tools-box.md).
 */

export type RailPoint = [number, number, number];

export type SmoothingAlgorithm =
  | 'catmullRom' | 'laplacian' | 'kalman' | 'kalmanBidirectional' | 'movingAverage' | 'gaussian'
  | 'exponential' | 'savitzkyGolay' | 'doublePass' | 'taubin' | 'scaleUp';

export interface SmoothingParam {
  title: string;
  /** The slider runs 0–10 and its value times this is the parameter (`MultiplierUpdate`). */
  multiplier: number;
  /** The desktop truncates this parameter to a whole number before using it. */
  integer?: boolean;
  /** Where the slider's 0 lands, added before truncation; the desktop has none (0). */
  min?: number;
  /** The most the parameter can be, for a range moved up by `min`. */
  max?: number;
}

export interface SmoothingAlgorithmInfo {
  id: SmoothingAlgorithm;
  label: string;
  params: SmoothingParam[];
}

/** The dropdown, in the desktop's order, with `RailSmoothingFunctions.MultiplierUpdate`'s sliders. */
export const SMOOTHING_ALGORITHMS: readonly SmoothingAlgorithmInfo[] = [
  // The desktop's 0–4 included 0, which adds nothing; here the four quarters of the slider are 1–4.
  { id: 'catmullRom', label: 'Catmull-Rom', params: [{ title: 'Max Subdivisions', multiplier: 0.4, integer: true, min: 1, max: 4 }] },
  { id: 'laplacian', label: 'Laplacian', params: [{ title: 'Iterations', multiplier: 1, integer: true }, { title: 'Alpha', multiplier: 0.3 }] },
  { id: 'kalman', label: 'Kalman', params: [{ title: 'Process Noise', multiplier: 0.1 }, { title: 'Measurement Noise', multiplier: 0.1 }] },
  { id: 'kalmanBidirectional', label: 'Kalman Bidirectional', params: [{ title: 'Process Noise', multiplier: 0.1 }, { title: 'Measurement Noise', multiplier: 0.1 }] },
  { id: 'movingAverage', label: 'Moving Average', params: [{ title: 'Window Size', multiplier: 1, integer: true }] },
  { id: 'gaussian', label: 'Gaussian', params: [{ title: 'Sigma', multiplier: 0.5 }] },
  { id: 'exponential', label: 'Exponential', params: [{ title: 'Alpha', multiplier: 0.15 }] },
  { id: 'savitzkyGolay', label: 'Savitzky-Golay', params: [{ title: 'Window Size', multiplier: 5, integer: true }, { title: 'Polynomial Order', multiplier: 2, integer: true }] },
  { id: 'doublePass', label: 'Double Pass', params: [{ title: 'Passes', multiplier: 2, integer: true }, { title: 'Strength', multiplier: 0.1 }] },
  { id: 'taubin', label: 'Taubin', params: [{ title: 'Lambda', multiplier: 0.2 }, { title: 'Mu', multiplier: -0.2 }, { title: 'Max Deviation', multiplier: 0.2 }] },
  { id: 'scaleUp', label: 'Scale Up', params: [{ title: 'Percent', multiplier: 10 }] },
];

export function algorithmInfo(id: SmoothingAlgorithm): SmoothingAlgorithmInfo {
  return SMOOTHING_ALGORITHMS.find(a => a.id === id) ?? SMOOTHING_ALGORITHMS[0];
}

/** A slider position (0–10) as the algorithm's parameter value. */
export function paramValue(param: SmoothingParam, slider: number): number {
  const raw = (param.min ?? 0) + slider * param.multiplier;
  const v = param.integer ? Math.trunc(raw) : raw;
  return param.max === undefined ? v : Math.min(param.max, v);
}

/** The desktop clamps every smoothed X and Y to ±5 (`RunSmoothingAlgorithm`); it left the head out. */
export const SMOOTHING_XY_BOUND = 5;

export interface SmoothingOptions {
  algorithm: SmoothingAlgorithm;
  /** Slider positions, 0–10 each; only as many as the algorithm has parameters are read. */
  sliders: [number, number, number];
  /** Lock Endpoints: the first and last points do not move. */
  anchor: boolean;
  /** Training Wheels Off (Catmull-Rom only): new points are not aligned to valid beats. */
  trainingWheelsOff: boolean;
  /** Scale-up buttons: the accumulated X and Y percentages (5, 10, …); 0 when not used. */
  scaleX?: number;
  scaleY?: number;
  bpm: number;
}

/**
 * `RunSmoothingAlgorithm`: one smoothing of the original points. The scale-up buttons take over from
 * the selected algorithm, as they do in the desktop editor.
 */
export function smoothRail(points: readonly RailPoint[], o: SmoothingOptions): RailPoint[] {
  if (points.length < 2) return points.map(p => [...p] as RailPoint);
  const info = algorithmInfo(o.algorithm);
  const [p1, p2, p3] = info.params.map((p, i) => paramValue(p, o.sliders[i] ?? 0));
  let out: RailPoint[];
  if ((o.scaleX ?? 0) > 0 || (o.scaleY ?? 0) > 0) {
    out = enlargeLine(points, ((o.scaleX ?? 0) + 100) / 100, ((o.scaleY ?? 0) + 100) / 100);
  } else {
    switch (o.algorithm) {
      case 'catmullRom':
        out = o.trainingWheelsOff ? catmullRomSmooth(points, p1) : catmullRomAligned(points, p1, o.bpm);
        break;
      case 'laplacian': out = o.anchor ? laplacianAnchored(points, p1, p2) : laplacianUnanchored(points, p1, p2); break;
      case 'kalman': out = kalman(points, p1, p2, o.anchor); break;
      case 'kalmanBidirectional': out = kalmanBidirectional(points, p1, p2, o.anchor); break;
      case 'movingAverage': out = movingAverage(points, p1, o.anchor); break;
      case 'gaussian': out = gaussian(points, p1, o.anchor); break;
      case 'exponential': out = o.anchor ? exponentialAnchored(points, p1) : exponentialUnanchored(points, p1); break;
      case 'savitzkyGolay': out = savitzkyGolay(points, p1, p2, o.anchor); break;
      case 'doublePass': out = doublePass(points, p1, p2, o.anchor); break;
      case 'taubin': out = taubin(points, p1, p2, p3, o.anchor); break;
      case 'scaleUp': out = enlargeLine(points, (p1 + 100) / 100, (p1 + 100) / 100); break;
      default: out = points.map(p => [...p] as RailPoint);
    }
  }
  const b = SMOOTHING_XY_BOUND;
  return out.map(([x, y, z]) => [Math.max(-b, Math.min(b, x)), Math.max(-b, Math.min(b, y)), z]);
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const copy = (pts: readonly RailPoint[]): RailPoint[] => pts.map(p => [...p] as RailPoint);
const withXY = (p: RailPoint, x: number, y: number): RailPoint => [x, y, p[2]];
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Unity's `Mathf.Approximately`. */
const approx = (a: number, b: number) => Math.abs(b - a) < Math.max(1e-6 * Math.max(Math.abs(a), Math.abs(b)), 1.1754944e-38 * 8);

// ── Beat alignment (BPMZoneHandler / Track.IsValidBeat, one BPM zone) ──────
//
// The desktop counts 64 zone beats to a beat; a valid zone beat is a whole one (a 64th) or a whole
// number of 4/3 (a 48th, a triplet step).

const zoneBeatOfZ = (z: number, bpm: number) => (z / 0.02 / 1000) * (bpm / 60) * 64;
const zOfZoneBeat = (zb: number, bpm: number) => (zb / 64 / (bpm / 60)) * 1000 * 0.02;
const roundToThird = (v: number) => Math.round(3 * v) / 3;

export function isValidZoneBeat(zb: number): boolean {
  const b = roundToThird(zb);
  const whole = Math.trunc(b);
  if (Math.abs(whole - b) < 1e-6) return true;
  const frac = b - whole;
  let thirds = whole * 3;
  if (frac > 0.3) thirds++;
  if (frac > 0.6) thirds++;
  return thirds % 4 === 0;
}

function closeValidZoneBeat(zb: number, forward: boolean): number {
  let b = roundToThird(zb);
  for (let guard = 0; guard < 16 && !isValidZoneBeat(b); guard++) b = roundToThird(b + (forward ? 1 : -1) / 3);
  return b;
}

/** `FindClosestValidZoneBeat`: the nearer of the next valid zone beat each way, forward on a tie. */
export function closestValidZoneBeat(zb: number): number {
  const b = roundToThird(zb);
  const fwd = closeValidZoneBeat(b, true);
  const back = closeValidZoneBeat(b, false);
  return Math.abs(b - fwd) > Math.abs(b - back) ? back : fwd;
}

// ── Catmull-Rom ─────────────────────────────────────────────────────────────

/**
 * `ComputeSegmentSubdivisions`: each segment's share of `max` by its time length against the average,
 * quantised so near-equal segments round alike, capped at `max`, and at least 1 for a segment at least
 * 5 % of the average.
 */
export function segmentSubdivisions(points: readonly RailPoint[], max: number): number[] {
  const n = points.length - 1;
  const lengths = Array.from({ length: n }, (_, i) => Math.abs(points[i + 1][2] - points[i][2]));
  const total = lengths.reduce((a, b) => a + b, 0);
  if (total <= 0) return lengths.map(() => 0);
  const avg = total / n;
  return lengths.map(len => {
    const raw = max * (len / avg);
    const q = Math.round(raw / 1e-4) * 1e-4;
    let share = Math.floor(q + 0.5);
    if (share > max) share = max;
    if (share <= 0 && len >= avg * 0.05) share = 1;
    return share;
  });
}

function catmullXY(t: number, p0: RailPoint, p1: RailPoint, p2: RailPoint, p3: RailPoint): [number, number] {
  const t2 = t * t;
  const t3 = t2 * t;
  const f = (a: number, b: number, c: number, d: number) =>
    0.5 * ((-t3 + 2 * t2 - t) * a + (3 * t3 - 5 * t2 + 2) * b + (-3 * t3 + 4 * t2 + t) * c + (t3 - t2) * d);
  return [f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])];
}

/** The points with a reflected point before the first and after the last, for the end tangents. */
function extended(points: readonly RailPoint[]): RailPoint[] {
  const n = points.length;
  const refl = (a: RailPoint, b: RailPoint): RailPoint => [2 * a[0] - b[0], 2 * a[1] - b[1], 2 * a[2] - b[2]];
  return [refl(points[0], points[1]), ...points, refl(points[n - 1], points[n - 2])];
}

/** `CatmullRomSmooth` (Training Wheels Off): new points at even time steps, not aligned to beats. */
export function catmullRomSmooth(points: readonly RailPoint[], max: number): RailPoint[] {
  if (points.length < 2 || max < 1) return copy(points);
  const subs = segmentSubdivisions(points, max);
  const ext = extended(points);
  const out: RailPoint[] = [[...points[0]] as RailPoint];
  for (let i = 1; i < ext.length - 2; i++) {
    const [p0, p1, p2, p3] = [ext[i - 1], ext[i], ext[i + 1], ext[i + 2]];
    const s = subs[i - 1];
    if (s === 0) { out.push([...p2] as RailPoint); continue; }
    for (let j = 1; j <= s; j++) {
      const t = j / s;
      const [x, y] = catmullXY(t, p0, p1, p2, p3);
      out.push([x, y, lerp(p1[2], p2[2], t)]);
    }
  }
  return out;
}

/**
 * `CatmullRomSmoothAlignToBeat`, then `RunSmoothingAlgorithm`'s check: every new point sits on the
 * valid beat nearest its even share of the segment, at least a 64th from its neighbours, and a segment
 * takes the most subdivisions (up to its share) that fit. If any two points still land on one beat the
 * rail is returned unchanged. The desktop's check reassigned the list it was iterating over; this one
 * is a separate pass.
 */
export function catmullRomAligned(points: readonly RailPoint[], max: number, bpm: number): RailPoint[] {
  if (points.length < 2 || max < 1) return copy(points);
  const subs = segmentSubdivisions(points, max);
  const ext = extended(points);
  const out: RailPoint[] = [[...points[0]] as RailPoint];
  for (let i = 1; i < ext.length - 2; i++) {
    const [p0, p1, p2, p3] = [ext[i - 1], ext[i], ext[i + 1], ext[i + 2]];
    const zb1 = zoneBeatOfZ(p1[2], bpm);
    const zb2 = zoneBeatOfZ(p2[2], bpm);
    const range = zb2 - zb1;
    if (approx(range, 0)) { out.push([...p2] as RailPoint); continue; }
    const target = subs[i - 1];
    let level = target;
    for (let lv = target; lv >= 1; lv--) {
      if (lv === 1) { level = 1; break; }
      let fits = true;
      let prev = zb1;
      for (let j = 1; j < lv; j++) {
        const valid = closestValidZoneBeat(zb1 + (j / lv) * range);
        if (approx(valid, prev) || approx(valid, zb2) || valid - prev < 1 || zb2 - valid < 1) { fits = false; break; }
        prev = valid;
      }
      if (fits) { level = lv; break; }
    }
    if (level === 0) { out.push([...p2] as RailPoint); continue; }
    for (let j = 1; j <= level; j++) {
      if (j === level) { out.push([...p2] as RailPoint); continue; }
      const valid = closestValidZoneBeat(zb1 + (j / level) * range);
      const t = Math.max(0, Math.min(1, (valid - zb1) / range));
      const [x, y] = catmullXY(t, p0, p1, p2, p3);
      out.push([x, y, zOfZoneBeat(valid, bpm)]);
    }
  }
  // Every point on its nearest valid beat; two on one beat means there was no room, so nothing changes.
  const aligned = out.map((p): RailPoint => [p[0], p[1], zOfZoneBeat(closestValidZoneBeat(zoneBeatOfZ(p[2], bpm)), bpm)]);
  for (let i = 0; i < aligned.length - 1; i++) {
    if (Math.abs(zoneBeatOfZ(aligned[i][2], bpm) - zoneBeatOfZ(aligned[i + 1][2], bpm)) < 1e-4) return copy(points);
  }
  return aligned;
}

// ── Laplacian ───────────────────────────────────────────────────────────────

/** `LaplacianSmoothUnanchored`: each point moves `alpha` of the way from its ORIGINAL position toward
 *  the midpoint of its current neighbours; an end point uses itself as its missing neighbour. */
export function laplacianUnanchored(points: readonly RailPoint[], iterations: number, alpha: number): RailPoint[] {
  let result = copy(points);
  for (let it = 0; it < iterations; it++) {
    const next = copy(result);
    for (let i = 0; i < result.length; i++) {
      const left = i === 0 ? result[i] : result[i - 1];
      const right = i === result.length - 1 ? result[i] : result[i + 1];
      const o = points[i];
      next[i] = [lerp(o[0], (left[0] + right[0]) / 2, alpha), lerp(o[1], (left[1] + right[1]) / 2, alpha), o[2]];
    }
    result = next;
  }
  return result;
}

/** `LaplacianSmoothAnchored`: as unanchored, for the interior points only. */
export function laplacianAnchored(points: readonly RailPoint[], iterations: number, alpha: number): RailPoint[] {
  let result = copy(points);
  for (let it = 0; it < iterations; it++) {
    const next = copy(result);
    for (let i = 1; i < result.length - 1; i++) {
      const o = points[i];
      next[i] = [
        lerp(o[0], (result[i - 1][0] + result[i + 1][0]) / 2, alpha),
        lerp(o[1], (result[i - 1][1] + result[i + 1][1]) / 2, alpha), o[2],
      ];
    }
    result = next;
  }
  return result;
}

// ── Kalman ──────────────────────────────────────────────────────────────────

/** `KalmanFilter1D`: a constant-position filter that starts at the first measurement. */
export function kalman1D(m: readonly number[], q: number, r: number): number[] {
  const out = new Array<number>(m.length);
  let x = m[0];
  let p = 1;
  out[0] = x;
  for (let i = 1; i < m.length; i++) {
    const pp = p + q;
    const k = pp / (pp + r);
    x = x + k * (m[i] - x);
    p = (1 - k) * pp;
    out[i] = x;
  }
  return out;
}

/** `KalmanFilterSmoothing{Anchored,Unanchored}`. Both noises 0 would divide by zero, so the rail is
 *  unchanged then, as in the desktop. The anchored form needs three points. */
export function kalman(points: readonly RailPoint[], q: number, r: number, anchor: boolean): RailPoint[] {
  if ((q === 0 && r === 0) || points.length < (anchor ? 3 : 2)) return copy(points);
  const xs = kalman1D(points.map(p => p[0]), q, r);
  const ys = kalman1D(points.map(p => p[1]), q, r);
  return points.map((p, i) => (anchor && (i === 0 || i === points.length - 1)) ? [...p] as RailPoint : withXY(p, xs[i], ys[i]));
}

/** `KalmanFilterBidirectionalSmoothing{Anchored,Unanchored}`: a forward and a backward pass, averaged;
 *  anchored blends them by a smoothstep along the rail and pins both ends. */
export function kalmanBidirectional(points: readonly RailPoint[], q: number, r: number, anchor: boolean): RailPoint[] {
  if ((q === 0 && r === 0) || points.length < 2) return copy(points);
  const n = points.length;
  const pass = (axis: 0 | 1) => {
    const v = points.map(p => p[axis]);
    const f = kalman1D(v, q, r);
    const b = kalman1D([...v].reverse(), q, r).reverse();
    return v.map((_, i) => {
      if (!anchor) return (f[i] + b[i]) * 0.5;
      const t = i / (n - 1);
      const ss = (u: number) => u * u * (3 - 2 * u);
      const wf = ss(1 - t);
      const wb = ss(t);
      return (f[i] * wf + b[i] * wb) / (wf + wb);
    });
  };
  const xs = pass(0);
  const ys = pass(1);
  return points.map((p, i) => (anchor && (i === 0 || i === n - 1)) ? [...p] as RailPoint : withXY(p, xs[i], ys[i]));
}

// ── Moving average, Gaussian ────────────────────────────────────────────────

/** `MovingAverageSmoothing{Anchored,Unanchored}`: the mean of the points within half the window each
 *  side; anchored shrinks the window near the ends so it stays symmetric, and needs a window of 1+. */
export function movingAverage(points: readonly RailPoint[], window: number, anchor: boolean): RailPoint[] {
  const n = points.length;
  if (anchor && (n < 2 || window < 1)) return copy(points);
  const half = Math.trunc(window / 2);
  return points.map((p, i) => {
    if (anchor && (i === 0 || i === n - 1)) return [...p] as RailPoint;
    const h = anchor ? Math.min(half, Math.min(i, n - 1 - i)) : half;
    let sx = 0, sy = 0, c = 0;
    for (let j = i - h; j <= i + h; j++) if (j >= 0 && j < n) { sx += points[j][0]; sy += points[j][1]; c++; }
    return withXY(p, sx / c, sy / c);
  });
}

/** `GaussianSmoothing{Anchored,Unanchored}`: a Gaussian-weighted mean out to 3 sigma (index distance);
 *  sigma 0 changes nothing. */
export function gaussian(points: readonly RailPoint[], sigma: number, anchor: boolean): RailPoint[] {
  const n = points.length;
  if (sigma <= 0 || (anchor && n < 2)) return copy(points);
  const radius = Math.ceil(3 * sigma);
  return points.map((p, i) => {
    if (anchor && (i === 0 || i === n - 1)) return [...p] as RailPoint;
    const rr = anchor ? Math.min(radius, Math.min(i, n - 1 - i)) : radius;
    let sx = 0, sy = 0, ws = 0;
    for (let j = i - rr; j <= i + rr; j++) {
      if (j < 0 || j >= n) continue;
      const d = j - i;
      const w = Math.exp(-(d * d) / (2 * sigma * sigma));
      sx += points[j][0] * w; sy += points[j][1] * w; ws += w;
    }
    return withXY(p, sx / ws, sy / ws);
  });
}

// ── Exponential ─────────────────────────────────────────────────────────────

/** `ExponentialSmoothingUnanchored`: S = alpha·Y + (1 − alpha)·S, from the first point. */
export function exponentialUnanchored(points: readonly RailPoint[], alpha: number): RailPoint[] {
  if (!points.length) return [];
  let cx = points[0][0], cy = points[0][1];
  return points.map((p, i) => {
    if (i === 0) return [...p] as RailPoint;
    cx = alpha * p[0] + (1 - alpha) * cx;
    cy = alpha * p[1] + (1 - alpha) * cy;
    return withXY(p, cx, cy);
  });
}

/** `ExponentialSmoothingAnchored`: a forward and a backward pass over the interior, blended toward the
 *  backward pass along the rail, both ends fixed. */
export function exponentialAnchored(points: readonly RailPoint[], alpha: number): RailPoint[] {
  const n = points.length;
  if (n <= 2) return copy(points);
  const fwd: [number, number][] = [];
  let fx = points[0][0], fy = points[0][1];
  for (let i = 1; i < n - 1; i++) {
    fx = alpha * points[i][0] + (1 - alpha) * fx;
    fy = alpha * points[i][1] + (1 - alpha) * fy;
    fwd.push([fx, fy]);
  }
  const back: [number, number][] = new Array(n - 2);
  let bx = points[n - 1][0], by = points[n - 1][1];
  for (let i = n - 2; i > 0; i--) {
    bx = alpha * points[i][0] + (1 - alpha) * bx;
    by = alpha * points[i][1] + (1 - alpha) * by;
    back[i - 1] = [bx, by];
  }
  const out: RailPoint[] = [[...points[0]] as RailPoint];
  for (let k = 0; k < n - 2; k++) {
    const t = (k + 1) / (n - 1);
    out.push(withXY(points[k + 1], lerp(fwd[k][0], back[k][0], t), lerp(fwd[k][1], back[k][1], t)));
  }
  out.push([...points[n - 1]] as RailPoint);
  return out;
}

// ── Savitzky-Golay ──────────────────────────────────────────────────────────

/**
 * The value at offset 0 of the least-squares polynomial of degree `order` through `(t, v)`. Offsets are
 * scaled to [-1, 1] before the fit, which keeps the normal equations well conditioned at high orders.
 */
export function polyFitAtZero(ts: readonly number[], vs: readonly number[], order: number): number {
  const deg = Math.min(order, ts.length - 1);
  const scale = Math.max(1, ...ts.map(Math.abs));
  const u = ts.map(t => t / scale);
  const m = deg + 1;
  const a: number[][] = Array.from({ length: m }, () => new Array(m + 1).fill(0));
  for (let k = 0; k < u.length; k++) {
    const pow: number[] = [1];
    for (let d = 1; d <= 2 * deg; d++) pow.push(pow[d - 1] * u[k]);
    for (let r = 0; r < m; r++) {
      for (let c = 0; c < m; c++) a[r][c] += pow[r + c];
      a[r][m] += pow[r] * vs[k];
    }
  }
  // Gaussian elimination with partial pivoting.
  for (let col = 0; col < m; col++) {
    let piv = col;
    for (let r = col + 1; r < m; r++) if (Math.abs(a[r][col]) > Math.abs(a[piv][col])) piv = r;
    [a[col], a[piv]] = [a[piv], a[col]];
    if (Math.abs(a[col][col]) < 1e-12) continue;
    for (let r = 0; r < m; r++) {
      if (r === col) continue;
      const f = a[r][col] / a[col][col];
      for (let c = col; c <= m; c++) a[r][c] -= f * a[col][c];
    }
  }
  return Math.abs(a[0][0]) < 1e-12 ? vs.reduce((s, v) => s + v, 0) / vs.length : a[0][m] / a[0][0];
}

/**
 * `SavitzkyGolaySmoothing{Anchored,Unanchored}` with a real fit. The desktop's "polynomial fit" was a
 * plain (unanchored) or centre-weighted (anchored) average, so Polynomial Order changed nothing; here
 * each point is the value at that point of the least-squares polynomial of that order over its window.
 * The windows are the desktop's: half the size each side, clipped at the ends (unanchored) or shrunk to
 * stay symmetric (anchored); a window no larger than the order is averaged, as there.
 */
export function savitzkyGolay(points: readonly RailPoint[], window: number, order: number, anchor: boolean): RailPoint[] {
  const n = points.length;
  if (anchor && (n < 2 || window < 1)) return copy(points);
  const half = Math.trunc(window / 2);
  return points.map((p, i) => {
    if (anchor && (i === 0 || i === n - 1)) return [...p] as RailPoint;
    const h = anchor ? Math.min(half, Math.min(i, n - 1 - i)) : half;
    const start = anchor ? i - h : Math.max(0, i - half);
    const end = anchor ? i + h : Math.min(n - 1, i + half);
    const size = end - start + 1;
    const idx = Array.from({ length: size }, (_, k) => start + k);
    if (size <= order) {
      return withXY(p, idx.reduce((s, j) => s + points[j][0], 0) / size, idx.reduce((s, j) => s + points[j][1], 0) / size);
    }
    const ts = idx.map(j => j - i);
    return withXY(p, polyFitAtZero(ts, idx.map(j => points[j][0]), order), polyFitAtZero(ts, idx.map(j => points[j][1]), order));
  });
}

// ── Double pass ─────────────────────────────────────────────────────────────

/** `DoublePassSmoothing{Anchored,Unanchored}`: each pass moves every point `strength` of the way to the
 *  mean of its immediate neighbours. */
export function doublePass(points: readonly RailPoint[], passes: number, strength: number, anchor: boolean): RailPoint[] {
  if (anchor && points.length < 2) return copy(points);
  let cur = copy(points);
  for (let pass = 0; pass < passes; pass++) {
    const n = cur.length;
    cur = cur.map((p, i, prev) => {
      if (anchor && (i === 0 || i === n - 1)) return [...p] as RailPoint;
      let sx = 0, sy = 0, c = 0;
      if (i > 0) { sx += prev[i - 1][0]; sy += prev[i - 1][1]; c++; }
      if (i < n - 1) { sx += prev[i + 1][0]; sy += prev[i + 1][1]; c++; }
      if (!c) return [...p] as RailPoint;
      return withXY(p, lerp(p[0], sx / c, strength), lerp(p[1], sy / c, strength));
    });
  }
  return cur;
}

// ── Taubin ──────────────────────────────────────────────────────────────────

/** Unanchored Taubin's Laplacian: neighbours up to two away, weighted 1 / (1 + distance). */
function laplacianAt(points: readonly RailPoint[], i: number): [number, number] {
  let sx = 0, sy = 0, ws = 0;
  for (let off = -2; off <= 2; off++) {
    const j = i + off;
    if (off === 0 || j < 0 || j >= points.length) continue;
    const w = 1 / (1 + Math.abs(off));
    sx += w * (points[j][0] - points[i][0]); sy += w * (points[j][1] - points[i][1]); ws += w;
  }
  return ws > 0 ? [sx / ws, sy / ws] : [0, 0];
}

/** Anchored Taubin's Laplacian: a window that narrows toward the ends, and 1.5× weight on the side away
 *  from the nearer end for the points next to it (`CalculateAdaptiveWeightedLaplacian`). */
function adaptiveLaplacianAt(points: readonly RailPoint[], i: number): [number, number] {
  const n = points.length;
  const dist = Math.min(i, n - 1 - i);
  const maxOff = dist === 0 ? 1 : dist === 1 ? 2 : 3;
  let sx = 0, sy = 0, ws = 0;
  for (let off = -maxOff; off <= maxOff; off++) {
    const j = i + off;
    if (off === 0 || j < 0 || j >= n) continue;
    let factor = 1;
    if (i === 1 && off > 0) factor = 1.5;
    else if (i === n - 2 && off < 0) factor = 1.5;
    else if (i === 0 || i === n - 1) factor = 0;
    const w = (1 / (1 + Math.abs(off))) * factor;
    sx += w * (points[j][0] - points[i][0]); sy += w * (points[j][1] - points[i][1]); ws += w;
  }
  return ws > 0 ? [sx / ws, sy / ws] : [0, 0];
}

function limitDeviation(p: RailPoint, o: RailPoint, max: number): RailPoint {
  const dx = p[0] - o[0];
  const dy = p[1] - o[1];
  const len = Math.hypot(dx, dy);
  if (len <= max || len === 0) return p;
  return [o[0] + (dx / len) * max, o[1] + (dy / len) * max, p[2]];
}

/**
 * `TaubinSmoothing{Anchored,Unanchored}`: five rounds of a lambda step then a mu step, each round
 * limited to `maxDeviation` from the original. Anchored uses the adaptive Laplacian and a deviation limit
 * that tightens toward the ends (×0.3, ×0.5, ×0.7 within one, two, three points), ends pinned.
 */
export function taubin(points: readonly RailPoint[], lambda: number, mu: number, maxDeviation: number, anchor: boolean): RailPoint[] {
  const n = points.length;
  if (n < 3) return copy(points);
  const step = (pts: RailPoint[], k: number): RailPoint[] => pts.map((p, i) => {
    if (anchor && (i === 0 || i === n - 1)) return p;
    const [lx, ly] = anchor ? adaptiveLaplacianAt(pts, i) : laplacianAt(pts, i);
    return [p[0] + k * lx, p[1] + k * ly, p[2]];
  });
  let cur = copy(points);
  for (let it = 0; it < 5; it++) {
    cur = step(step(cur, lambda), mu);
    cur = cur.map((p, i) => {
      if (!anchor) return limitDeviation(p, points[i], maxDeviation);
      if (i === 0 || i === n - 1) return [...points[i]] as RailPoint;
      const d = Math.min(i, n - 1 - i);
      const strength = d <= 1 ? 0.3 : d <= 2 ? 0.5 : d <= 3 ? 0.7 : 1;
      return limitDeviation(p, points[i], maxDeviation * strength);
    });
  }
  return cur;
}

// ── Scale up ────────────────────────────────────────────────────────────────

/**
 * `EnlargeLine`: every interior point's offset from the baseline is scaled by `sx` in X and `sy` in Y;
 * the first and last points stay. The desktop measured the offset from the nearest point of the 3-D
 * line through the ends, so time leaked into the X/Y offset and the scaled point drifted along the rail.
 * Here the baseline at a point's time is the straight X/Y path from the first point to the last.
 */
export function enlargeLine(points: readonly RailPoint[], sx: number, sy: number): RailPoint[] {
  const n = points.length;
  if (n < 3) return copy(points);
  const a = points[0];
  const b = points[n - 1];
  const span = b[2] - a[2];
  return points.map((p, i) => {
    if (i === 0 || i === n - 1) return [...p] as RailPoint;
    const t = span === 0 ? 0 : Math.max(0, Math.min(1, (p[2] - a[2]) / span));
    const bx = lerp(a[0], b[0], t);
    const by = lerp(a[1], b[1], t);
    return withXY(p, bx + (p[0] - bx) * sx, by + (p[1] - by) * sy);
  });
}
